import { useMemo } from "react";
import { usePermissions } from "./usePermissions";
import { firebaseHelper } from "./firebase";
import { Permissions } from "ts-interfaces";
import {
  useWorkspaceStore,
  getActiveDesignFirmId,
} from "./workspace/useWorkspaceContext";

interface TeamMembership {
  teamId: string | null;
  isTeamMember: boolean;
  isTeamAdmin: boolean;
  permissions: Permissions | null;
  loading: boolean;
}

// The firm the user is acting in: the preferred (active-workspace) firm when
// they're a member of it, otherwise their first membership. The fallback
// preserves the original behavior for single-team users and for team members
// currently in their Personal workspace.
const resolveTeamId = (
  permissions: Permissions | null,
  preferredFirmId: string | null,
) => {
  if (!permissions) return null;
  const firmIds = [
    ...(permissions.teamsAdmin ?? []),
    ...(permissions.teamsDesigner ?? []),
  ];
  if (preferredFirmId && firmIds.includes(preferredFirmId))
    return preferredFirmId;
  return firmIds[0] ?? null;
};

const isAdminForTeam = (
  permissions: Permissions | null,
  teamId: string | null,
) => {
  if (!teamId || !permissions) return false;
  return permissions.teamsAdmin?.includes(teamId) ?? false;
};

/**
 * Hook to get team membership information for a user.
 *
 * `teamId` resolves to the ACTIVE workspace's firm for multi-team users (pass
 * `firmId` to pin a specific firm instead); a member acting outside any team
 * workspace falls back to their first membership.
 */
export const useTeamMembership = (
  uid?: string | null,
  firmId?: string | null,
): TeamMembership => {
  const { permissions, loading } = usePermissions(uid, firebaseHelper.db);

  // Subscribe to the workspace store so a switch re-renders consumers; the
  // value itself comes from getActiveDesignFirmId (store + localStorage).
  useWorkspaceStore((s) => (uid ? s.activeIdByUid[uid] : undefined));
  const preferredFirmId = firmId ?? getActiveDesignFirmId(uid);

  const teamId = useMemo(
    () => resolveTeamId(permissions, preferredFirmId),
    [permissions, preferredFirmId],
  );
  const isTeamMember = Boolean(teamId);
  const isTeamAdmin = useMemo(
    () => isAdminForTeam(permissions, teamId),
    [permissions, teamId],
  );

  return {
    teamId,
    isTeamMember,
    isTeamAdmin,
    permissions,
    loading,
  };
};
